import React, { useEffect, useRef, useState, useLayoutEffect, useCallback } from 'react';
import { useMinetestConsole, usePrefetchData, useStorageManager } from '../../utils/GlobalContext';
import SnackBar from '../SnackBar';
import { type GameOptions } from '../../App';
import { fixGeometry as fixGeometryHelper, queryProxy } from './helpers';
import PackManager from './packManager';

interface RuntimeScreenProps {
  gameOptions: GameOptions;
  onGameStatus: (status: 'running' | 'failed') => void;
  zipLoaderPromise: Promise<Uint8Array> | null;
  serverExitTimestamp: Date | null;
}

declare global {
  interface Window {
    emloop_ready?: () => void;
    emloop_request_animation_frame?: () => void;
    Module: any;
    emloop_pause: any;
    emloop_unpause: any;
    emloop_init_sound: any;
    emloop_invoke_main: any;
    emloop_install_pack: any;
    emloop_set_minetest_conf: any;
    irrlicht_want_pointerlock: any;
    irrlicht_force_pointerlock: any;
    irrlicht_resize: any;
    emsocket_init: any;
    emsocket_set_proxy: any;
    emsocket_set_vpn: any;
    cwrap: (name: string, returnType: string | null, argTypes: string[]) => any;
    stringToNewUTF8: (text: string) => number;
    _malloc: (size: number) => number;
    _free: (ptr: number) => void;
    HEAPU8: Uint8Array;
    HEAPU32: Uint32Array;
  }
}

// PiolaCraft Realms: worldmod que se inyecta en el mundo del realm al hostear.
// Protege al jugador REMOTO que se une: mientras el mapa le llega por la red lo
// congela unos segundos (sin gravedad no cae al vacío) y le anula el daño
// (sin asfixia mientras los bloques cargan). Al host local (127.0.0.1) no lo toca.
const PC_JOIN_GUARD_LUA = `-- pc_join_guard (PiolaCraft): proteccion de entrada para jugadores remotos.
local FREEZE_S = 12   -- congelado (sin gravedad/movimiento) mientras llega el mapa
local GRACE_S  = 30   -- inmune a danio
local grace = {}

local function is_local(name)
	local info = minetest.get_player_information and minetest.get_player_information(name)
	return info and (info.address == "127.0.0.1" or info.address == "::1")
end

local function unfreeze(name, entry_pos)
	local p = minetest.get_player_by_name(name)
	if not p then return end
	if minetest.get_modpath("playerphysics") and playerphysics then
		playerphysics.remove_physics_factor(p, "speed", "pc_join_guard")
		playerphysics.remove_physics_factor(p, "gravity", "pc_join_guard")
		playerphysics.remove_physics_factor(p, "jump", "pc_join_guard")
	else
		p:set_physics_override({ speed = 1, gravity = 1, jump = 1 })
	end
	-- Si durante la carga el cliente "cayo" al vacio, devolverlo a la entrada.
	local q = p:get_pos()
	if entry_pos and q and q.y < entry_pos.y - 16 then p:set_pos(entry_pos) end
	minetest.log("action", "[pc_join_guard] " .. name .. " liberado")
end

minetest.register_on_joinplayer(function(player)
	local name = player:get_player_name()
	if is_local(name) then return end
	grace[name] = minetest.get_us_time() + GRACE_S * 1000000
	local entry_pos = player:get_pos()
	if minetest.get_modpath("playerphysics") and playerphysics then
		playerphysics.add_physics_factor(player, "speed", "pc_join_guard", 0)
		playerphysics.add_physics_factor(player, "gravity", "pc_join_guard", 0)
		playerphysics.add_physics_factor(player, "jump", "pc_join_guard", 0)
	else
		player:set_physics_override({ speed = 0, gravity = 0, jump = 0 })
	end
	minetest.chat_send_player(name, "Cargando el mundo... en unos segundos ya podes moverte.")
	minetest.after(FREEZE_S, function() unfreeze(name, entry_pos) end)
	minetest.log("action", "[pc_join_guard] protegiendo a " .. name)
end)

minetest.register_on_leaveplayer(function(player)
	grace[player:get_player_name()] = nil
end)

minetest.register_on_player_hpchange(function(player, hp_change, reason)
	local g = grace[player:get_player_name()]
	if g and hp_change < 0 then
		if minetest.get_us_time() < g then return 0 end
		grace[player:get_player_name()] = nil
	end
	return hp_change
end, true)

minetest.log("action", "[pc_join_guard] cargado")
`;

// PiolaCraft Realms: worldmod de ESTADÍSTICAS del realm — tiempo jugado y
// muertes por jugador, persistidos en mod storage (viajan en el ZIP del realm)
// y volcados a <mundo>/pc_stats.json para que el host los suba al backend.
const PC_REALM_STATS_LUA = `-- pc_realm_stats (PiolaCraft): dias jugados y muertes por jugador.
local S = minetest.get_mod_storage()
local path = minetest.get_worldpath() .. "/pc_stats.json"
local stats = minetest.parse_json(S:get_string("stats") or "") or {}
if type(stats) ~= "table" then stats = {} end
stats.players = stats.players or {}
stats.created = stats.created or os.time()
local online = {}

local function flush()
	local txt = minetest.write_json(stats) or "{}"
	S:set_string("stats", txt)
	minetest.safe_file_write(path, txt)
end

local function accum(name)
	local t = online[name]
	if not t then return end
	local p = stats.players[name] or { secs = 0, deaths = 0 }
	p.secs = (p.secs or 0) + math.max(0, os.time() - t)
	stats.players[name] = p
	online[name] = os.time()
end

-- OJO: NADA de escrituras al cargar el mod ni en join — escribir archivos del
-- mundo durante el arranque del server dispara la persistencia del loader
-- (readFile en el hilo principal) y traba el open de las bases sqlite
-- ("database is locked" → el mundo no arranca). Todo diferido al globalstep.
minetest.register_on_joinplayer(function(pl)
	local name = pl:get_player_name()
	online[name] = os.time()
	if not stats.players[name] then stats.players[name] = { secs = 0, deaths = 0 } end
end)

minetest.register_on_leaveplayer(function(pl)
	local name = pl:get_player_name()
	accum(name)
	online[name] = nil
end)

minetest.register_on_dieplayer(function(pl)
	local name = pl:get_player_name()
	local p = stats.players[name] or { secs = 0, deaths = 0 }
	p.deaths = (p.deaths or 0) + 1
	stats.players[name] = p
end)

local timer = 0
minetest.register_globalstep(function(dt)
	timer = timer + dt
	if timer >= 30 then
		timer = 0
		for name in pairs(online) do accum(name) end
		flush()
	end
end)

minetest.register_on_shutdown(function()
	for name in pairs(online) do accum(name) end
	flush()
end)

minetest.log("action", "[pc_realm_stats] cargado")
`;

// PiolaCraft Realms: worldmod de PRIVILEGIOS — en realms CREATIVOS todos los que
// entran reciben fly/fast (como el creativo de Minecraft). Sin esto, volar
// dependía de ser "admin" del server (= el que hostea esa sesión): al re-prender
// o al rotar de host, el privilegio se perdía ("sin privilegio de vuelo").
// Se re-otorga en CADA join, así no depende de lo que traiga auth.sqlite.
const PC_REALM_PRIVS_LUA = `-- pc_realm_privs (PiolaCraft): vuelo para todos en realms creativos.
if minetest.settings:get_bool("creative_mode") then
	minetest.register_on_joinplayer(function(pl)
		local name = pl:get_player_name()
		local privs = minetest.get_player_privs(name)
		if not (privs.fly and privs.fast) then
			privs.fly = true
			privs.fast = true
			minetest.set_player_privs(name, privs)
			minetest.log("action", "[pc_realm_privs] fly/fast para " .. name)
		end
	end)
end
`;

const RuntimeScreen: React.FC<RuntimeScreenProps> = ({ gameOptions, onGameStatus, zipLoaderPromise, serverExitTimestamp }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  // Pantalla: arranca con lo guardado por el menú de Opciones del juego
  // (localStorage pcDisplay) — así la elección persiste entre sesiones.
  const pcDisplay = (() => {
    try { return JSON.parse(localStorage.getItem('pcDisplay') || '{}'); }
    catch (e) { return {}; }
  })();
  const [showConsole, setShowConsole] = useState(pcDisplay.console === '1');
  const [resolution, setResolution] = useState(pcDisplay.res || 'high');
  // La relación de aspecto NO persiste: el juego siempre arranca llenando la
  // ventana ("Ajustar a la pantalla"); 4:3/16:9/1:1 valen solo en la sesión.
  // (Un 4:3 guardado dejaba al usuario con bandas negras "para siempre".)
  const [aspectRatio, setAspectRatio] = useState('any');
  const storageManager = useStorageManager();
  const prefetchData = usePrefetchData();
  const minetestConsole = useMinetestConsole();
  const [isLoading, setIsLoading] = useState(true);
  const [packManager] = useState(() => new PackManager(minetestConsole));
  const [vpnServerCode, setVpnServerCode] = useState<string | null>(null);
  const [vpnClientCode, setVpnClientCode] = useState<string | null>(null);
  // Selector de mundos (modo host): al entrar mostramos la lista de mundos guardados
  // + crear/elegir/borrar, en vez de tirar directo a un mundo autogenerado.
  const [showWorldPicker, setShowWorldPicker] = useState(false);
  const [worldList, setWorldList] = useState<string[]>([]);
  const [newWorldName, setNewWorldName] = useState('');
  const chosenWorldRef = useRef<string | null>(null);

  // Function to fix canvas geometry based on selected options
  const resolutionRef = useRef(resolution);
  const aspectRatioRef = useRef(aspectRatio);
  resolutionRef.current = resolution;
  aspectRatioRef.current = aspectRatio;
  const fixGeometry = useCallback(() => {
    if (!canvasRef.current || !canvasContainerRef.current) return;
    fixGeometryHelper(canvasRef.current, canvasContainerRef.current, resolution, aspectRatio);
  }, []);

  // Handle aspect ratio change
  // Persistir pantalla en el mismo lugar que usa el menú de Opciones del juego.
  const saveDisplay = (k: string, v: string) => {
    try {
      const d = JSON.parse(localStorage.getItem('pcDisplay') || '{}');
      d[k] = v;
      localStorage.setItem('pcDisplay', JSON.stringify(d));
    } catch (e) { /* */ }
  };
  // Toggle console visibility
  const toggleConsole = useCallback(() => {
    setShowConsole(prev => {
      saveDisplay('console', !prev ? '1' : '0');
      return !prev;
    });
  }, []);

  // Set minetest.conf options by writing directly to the WASM virtual filesystem
  // The Emscripten build uses path_user = "/minetest" (see porting.cpp)
  const setMinetestConf = useCallback((config: Record<string, string>) => {
    const confLines = [];
    for (const [key, value] of Object.entries(config)) {
      confLines.push(`${key} = ${value}`);
    }
    if (confLines.length === 0) return;
    const confTxt = confLines.join('\n') + '\n';
    // Motor con emloop (mío): setea la conf en memoria. Motor sin él (Gemini):
    // escribe el archivo directo en el FS. Robusto para ambos.
    if (window.emloop_set_minetest_conf && window.stringToNewUTF8) {
      const confBuf = window.stringToNewUTF8(confTxt);
      window.emloop_set_minetest_conf(confBuf);
      window._free(confBuf);
      minetestConsole.print(`Set config (emloop):\n${confTxt}`);
    } else if (window.Module && window.Module.FS) {
      try { window.Module.FS.mkdir('/minetest'); } catch (e) { /* ya existe */ }
      window.Module.FS.writeFile('/minetest/minetest.conf', confTxt);
      minetestConsole.print(`Set config (FS):\n${confTxt}`);
    } else {
      minetestConsole.printErr("RuntimeScreen: setMinetestConf failed");
    }
  }, []);

  // Apply proxy setting
  const setProxy = useCallback((proxyUrl: string) => {
    if (window.emsocket_set_proxy && window.stringToNewUTF8) {
      const proxyBuf = window.stringToNewUTF8(proxyUrl);
      window.emsocket_set_proxy(proxyBuf);
      window._free(proxyBuf);
      minetestConsole.print(`RuntimeScreen: setProxy ${proxyUrl}`);
    }
    else {
      minetestConsole.printErr("RuntimeScreen: setProxy failed");
    }
  }, []);

  // Apply VPN settings
  const setVpn = useCallback((serverCode: string | null, clientCode: string | null) => {
    console.log('setVpn', serverCode, clientCode);
    setVpnServerCode(serverCode);
    setVpnClientCode(clientCode);
    // Use serverCode when available, otherwise use clientCode
    const code = serverCode || clientCode;
    if (code !== null) {
      const vpnBuf = window.stringToNewUTF8(code);
      window.emsocket_set_vpn(vpnBuf);
      window._free(vpnBuf);
    }
  }, []);

  // Function to create argv for main
  const makeArgv = useCallback((args: string[]) => {
    if (!window._malloc || !window.HEAPU8 || !window.stringToNewUTF8) {
      minetestConsole.printErr("Error: Required Emscripten functions not available");
      return [0, 0];
    }

    // Allocate memory for pointers (4 bytes per pointer)
    const argv = window._malloc((args.length + 1) * 4);
    let i;
    for (i = 0; i < args.length; i++) {
      window.HEAPU32[(argv >>> 2) + i] = window.stringToNewUTF8(args[i]);
    }
    window.HEAPU32[(argv >>> 2) + i] = 0; // argv[argc] == NULL
    return [i, argv];
  }, []);

  // Launch the game with the current settings
  const launchGameRef = useRef<(() => Promise<void>) | null>(null);
  launchGameRef.current = (async () => {
    try {
      if (!storageManager) {
        throw new Error('StorageManager does not exist');
      }
      if (!(await storageManager.isInitialized)) {
        // Initialize the storage manager with the selected storage policy
        await storageManager.initialize(
          { policy: gameOptions.storagePolicy },
          minetestConsole,
        );
      }
      if (zipLoaderPromise) {
        const zipFile = await zipLoaderPromise;
        await storageManager.restoreFromZip(zipFile);
      }
      // PiolaCraft Realms: si arrancamos como HOST de un realm, bajamos su mundo
      // compartido del backend y lo restauramos en /minetest/worlds/<realmId> ANTES
      // de cargar a WASMFS. 204 = realm nuevo → arranca con mundo fresco. Acotado a
      // la carpeta del realm (restoreRealmWorld NO borra los mundos del usuario).
      if (gameOptions.mode === 'host' && gameOptions.realmId) {
        try {
          const rid = gameOptions.realmId;
          minetestConsole.print(`Realm: bajando mundo compartido (${rid})...`);
          const resp = await fetch(`/api/pc/realms/${encodeURIComponent(rid)}/world`, { credentials: 'include' });
          if (resp.status === 200) {
            const buf = new Uint8Array(await resp.arrayBuffer());
            if (buf.length) {
              await storageManager.restoreRealmWorld(rid, buf);
              minetestConsole.print(`Realm: mundo restaurado (${buf.length}b)`);
            }
          } else {
            minetestConsole.print(`Realm: sin mundo previo (HTTP ${resp.status}) → mundo nuevo`);
          }
        } catch (e) {
          minetestConsole.printErr('Realm: error bajando el mundo: ' + e);
        }
      }
      if (!storageManager.hasCopiedToModuleFS) {
        await storageManager.copyToModuleFS();
      }
      // PiolaCraft Realms: inyectar el worldmod pc_join_guard en el mundo del
      // realm (los worldmods cargan siempre, sin tocar world.mt). Se escribe en
      // cada boot del host, así no depende de que venga en el ZIP compartido.
      // OJO: pre-crear la carpeta del mundo SIN un world.mt válido rompe el boot
      // ("No se puede encontrar o cargar el juego": el motor ve un mundo
      // existente sin gameid). Si world.mt no existe (realm fresco), lo
      // escribimos nosotros con el gameid correcto.
      if (gameOptions.mode === 'host' && gameOptions.realmId) {
        try {
          const FS = (window as any).Module.FS;
          const base = '/minetest/worlds/' + gameOptions.realmId;
          for (const d of [base, base + '/worldmods', base + '/worldmods/pc_join_guard']) {
            try { FS.mkdir(d); } catch (e) { /* ya existe */ }
          }
          const write = (p: string, txt: string) => {
            // El FS.writeFile de este build APPENDEA: borrar antes de escribir.
            try { FS.unlink(p); } catch (e) { /* no existía */ }
            FS.writeFile(p, txt);
          };
          let hasWorldMt = false;
          try { FS.stat(base + '/world.mt'); hasWorldMt = true; } catch (e) { /* no existe */ }
          if (!hasWorldMt) {
            // Config elegida al crear el realm: "modo,daño,plano" (viene en ?rcfg=).
            const [rMode, rDmg, rFlat] = (gameOptions.realmCfg || 'survival,1,0').split(',');
            const creative = rMode === 'creative';
            const hardcore = rMode === 'hardcore';
            const damage = creative ? false : (hardcore ? true : rDmg !== '0');
            const flat = rFlat === '1';
            write(base + '/world.mt',
              'gameid = ' + (gameOptions.gameId || 'mineclone2') + '\n' +
              'world_name = ' + gameOptions.realmId + '\n' +
              'backend = sqlite3\n' +
              'player_backend = sqlite3\n' +
              'auth_backend = sqlite3\n' +
              'mod_storage_backend = sqlite3\n' +
              'creative_mode = ' + creative + '\n' +
              'enable_damage = ' + damage + '\n' +
              'load_mod_yolo = ' + hardcore + '\n' +
              (flat ? 'load_mod_flatland = true\nmg_name = singlenode\n' : 'mg_name = v7\n'));
            minetestConsole.print('Realm: world.mt inicial escrito (' + rMode + (flat ? ', plano' : '') + ')');
          }
          write(base + '/worldmods/pc_join_guard/mod.conf',
            'name = pc_join_guard\ndescription = Protege a los que se unen mientras carga el mapa (PiolaCraft)\n');
          write(base + '/worldmods/pc_join_guard/init.lua', PC_JOIN_GUARD_LUA);
          try { FS.mkdir(base + '/worldmods/pc_realm_stats'); } catch (e) { /* ya existe */ }
          write(base + '/worldmods/pc_realm_stats/mod.conf',
            'name = pc_realm_stats\ndescription = Dias jugados y muertes por jugador del realm (PiolaCraft)\n');
          write(base + '/worldmods/pc_realm_stats/init.lua', PC_REALM_STATS_LUA);
          try { FS.mkdir(base + '/worldmods/pc_realm_privs'); } catch (e) { /* ya existe */ }
          write(base + '/worldmods/pc_realm_privs/mod.conf',
            'name = pc_realm_privs\ndescription = Vuelo para todos en realms creativos (PiolaCraft)\n');
          write(base + '/worldmods/pc_realm_privs/init.lua', PC_REALM_PRIVS_LUA);
          minetestConsole.print('Realm: worldmods pc_join_guard + pc_realm_stats + pc_realm_privs inyectados');
        } catch (e) { minetestConsole.printErr('Realm: no pude inyectar pc_join_guard: ' + e); }
      }
      minetestConsole.print(`Storage initialized with policy: ${gameOptions.storagePolicy}`);

      // Always install the base and voxelibre packs
      minetestConsole.print("Installing required game packs...");
      await packManager.addPack('base', prefetchData.result.base!);
      if (gameOptions.gameId === 'mineclone2') {
        await packManager.addPack('voxelibre', prefetchData.result.voxelibre!);
      }
      else if (gameOptions.gameId === 'mineclonia') {
        await packManager.addPack('mineclonia', prefetchData.result.mineclonia!);
      }
      else if (gameOptions.gameId === 'minetest_game') {
        await packManager.addPack('minetest_game', prefetchData.result.minetest_game!);
      }
      else if (gameOptions.gameId === 'glitch') {
        await packManager.addPack('glitch', prefetchData.result.glitch!);
      }
      else if (gameOptions.gameId === 'blockbomber') {
        await packManager.addPack('blockbomber', prefetchData.result.blockbomber!);
      }

      // Set canvas size
      if (canvasRef.current) fixGeometry();

      // Create config
      const conf: Record<string, string> = {
        'viewing_range': '140',
        'max_block_send_distance': '10',
        'max_block_generate_distance': '10',
        'block_send_optimize_distance': '10',
        'client_mapblock_limit': '5000',
        'no_mtg_notification': 'true',
        'language': gameOptions.language,
        'mcl_playersSleepingPercentage': '1',
        'secure.trusted_mods': 'pc_skin_apply,vl_trusted',
        // PiolaCraft: nombre del jugador = usuario de la plataforma (?pcname=).
        'name': gameOptions.playerName || 'Jugador',
        // PiolaCraft: keybinds leídos al boot.
        'keymap_inventory': 'KEY_KEY_E',   // abrir inventario con E
        'doubletap_jump': 'true',          // doble-espacio para volar
        // Luanti 5.9 renombró special1 -> aux1; el nombre viejo era ignorado
        // y aux1 quedaba en su default (E), pisándose con el inventario.
        'keymap_aux1': 'KEY_MBUTTON',      // pick block (rueda del mouse)
        'keymap_zoom': 'KEY_KEY_B',
        'keymap_pc_dash': 'KEY_KEY_Z',     // dash (propulsión hacia la mira)
        'fast_move': 'false',
        // Pack de texturas Craft & Ruin (viaja en base.pack): el motor pisa
        // texturas por nombre de archivo, con búsqueda recursiva en el dir.
        'texture_path': '/minetest/textures/craft_and_ruin',
        // PiolaCraft: fuente pixel VT323 (SIN esto el render WebGL colapsa).
        'font_path': '/minetest/fonts/VT323-Regular.ttf',
        'font_path_italic': '/minetest/fonts/VT323-Regular.ttf',
        'font_path_bold': '/minetest/fonts/VT323-Regular.ttf',
        'font_path_bold_italic': '/minetest/fonts/VT323-Regular.ttf',
        'mono_font_path': '/minetest/fonts/VT323-Regular.ttf',
        'mono_font_path_italic': '/minetest/fonts/VT323-Regular.ttf',
        'mono_font_path_bold': '/minetest/fonts/VT323-Regular.ttf',
        'mono_font_path_bold_italic': '/minetest/fonts/VT323-Regular.ttf',
        // Letra base "chica" fija: el tamaño lo maneja gui_scaling (opción
        // "Tamaño de letra"), que escala letra y botones juntos.
        'font_size': '22',
        'font_shadow': '1',
        'font_shadow_alpha': '164',
      };
      if (gameOptions.mode === 'host' || gameOptions.mode === 'join') {
        conf['viewing_range'] = '90';
        conf['max_block_send_distance'] = '5';
        conf['max_block_generate_distance'] = '5';
        conf['block_send_optimize_distance'] = '5';
        conf['client_mapblock_limit'] = '5000';
      }
      if (gameOptions.mode === 'host') {
        // Puerto fijo para que los amigos (join → 172.16.0.1:30000) conecten.
        conf['port'] = '30000';
        // Modelo "LAN desde el arranque": TODO mundo solitario es host.
        conf['viewing_range'] = '120';
        conf['max_block_send_distance'] = '5';
        conf['max_block_generate_distance'] = '5';
        conf['block_send_optimize_distance'] = '5';
        // max_packets_per_iteration: la media pesada YA NO viaja por el relay (el que se
        // une la reusa de su copia local, parche en clientmedia.cpp), así que lo único
        // que va por el relay es el MAPA + sync (chico). 256 era demasiado bajo y ahogaba
        // el envío del mapa al que se une (peer compite con el cliente local) → mundo
        // negro / no se mueve. 1024 (default de Minetest) deja fluir el mapa sin saturar.
        conf['max_packets_per_iteration'] = '1024';
        // El server corre en un navegador: con el default (active_block_range=4)
        // los ABMs de VoxeLibre procesaban ~970 bloques activos con 2 jugadores y
        // cada tick tardaba 200-340ms → los ítems dropeados aparecían tarde y todo
        // el sync se sentía lageado. Bajar el rango de bloques activos achica ese
        // trabajo sin afectar el envío del mapa (max_block_send_distance sigue en 5).
        conf['active_block_range'] = '2';
        conf['abm_interval'] = '2';
        conf['active_object_send_range_blocks'] = '4';
        // Guardar el mapa al FS cada 3s (default 5.3): si el host se congela
        // (pestaña de fondo) o muere, la última subida al backend carga como
        // mucho ~3s de construcciones perdidas. En WASMFS (memoria) es barato.
        conf['server_map_save_interval'] = '3';
        // Realm: los globales creative/daño/mapgen tienen que acompañar al world.mt
        // (el flujo de mundos locales hace lo mismo leyendo world.mt antes de start;
        // acá la config viene del realm). El mg_name solo pesa en el PRIMER boot
        // (después manda map_meta.txt del mundo ya generado).
        if (gameOptions.realmId) {
          const [rMode, rDmg, rFlat] = (gameOptions.realmCfg || 'survival,1,0').split(',');
          const creative = rMode === 'creative';
          conf['creative_mode'] = String(creative);
          conf['enable_damage'] = String(creative ? false : (rMode === 'hardcore' ? true : rDmg !== '0'));
          conf['mg_name'] = rFlat === '1' ? 'singlenode' : 'v7';
        }
      }
      // Preferencias del menú de Opciones del juego: el loader reescribe este
      // conf entero en cada boot, así que acá se re-aplican las guardadas
      // (localStorage pcPrefs, las manda el juego vía acción "pref:k=v").
      // Solo claves de la lista blanca — nunca pisan name/port/fuentes/etc.
      try {
        const prefs = JSON.parse(localStorage.getItem('pcPrefs') || '{}');
        // font_size queda FUERA a propósito: la base es fija (22) y un 26/30
        // viejo guardado la pisaría.
        const allow = ['viewing_range', 'sound_volume',
          'mute_sound', 'smooth_lighting', 'enable_particles', 'enable_3d_clouds',
          'leaves_style', 'doubletap_jump', 'fov', 'pc_music_volume',
          'pc_voice_volume'];
        for (const k of allow) {
          if (typeof prefs[k] === 'string' && prefs[k] !== '') conf[k] = prefs[k];
        }
        // gui_scaling lo maneja "Tamaño de letra" (Chica/Normal/Grande) y
        // escala letra + botones juntos. SIEMPRE se manda explícito y solo
        // con los valores exactos del menú: el engine persiste su PROPIO
        // minetest.conf en IndexedDB y ahí quedó un gui_scaling viejo (de la
        // opción eliminada "Tamaño de la interfaz") que agrandaba TODO el
        // menú hasta desbordar — si la clave no viaja en este conf, la
        // basura persistida gana.
        conf['gui_scaling'] = ['1.0', '1.1'].includes(prefs['gui_scaling'])
          ? prefs['gui_scaling'] : '1.0';
        // Distancia de render: tope 160 — con 200 el WASM baja tanto los FPS
        // que los mobs se mueven "a saltos" (parecía knockback roto).
        const vr = parseInt(conf['viewing_range'], 10);
        if (!isNaN(vr) && vr > 160) conf['viewing_range'] = '160';
      } catch (e) { /* prefs corruptas: defaults */ }
      // Teclas re-mapeadas en "Combinaciones de teclas" (diálogo del engine):
      // este conf se reescribe ENTERO en cada boot, así que sin esto todo
      // re-mapeo se perdería al recargar. El poll del puente las captura a
      // pcKeymap; acá se re-aplican. Solo claves keymap_* y los toggles de
      // ese mismo diálogo (nunca pisan otra cosa).
      try {
        const km = JSON.parse(localStorage.getItem('pcKeymap') || '{}');
        for (const k of Object.keys(km)) {
          if (/^(keymap_[a-z0-9_]+|doubletap_jump|aux1_descends|autojump)$/.test(k)
              && typeof km[k] === 'string') {
            conf[k] = km[k];
          }
        }
      } catch (e) { /* mapa corrupto: teclas default */ }
      // Estado actual de PANTALLA (res/aspecto/consola): informativo, para que
      // el menú de Opciones del juego muestre lo que está activo.
      try {
        const disp = JSON.parse(localStorage.getItem('pcDisplay') || '{}');
        conf['pc_display_res'] = disp.res || 'high';
        // El aspecto no persiste: cada boot arranca en "Ajustar a la pantalla".
        conf['pc_display_aspect'] = 'any';
        conf['pc_display_console'] = disp.console || '0';
      } catch (e) { /* */ }

      // [PC] Perfil de BAJA MEMORIA para NAVEGADOR MÓVIL (Android/iPhone).
      // El heap del WASM es fijo (~1,9 GB) y se comita a medida que se usa; en
      // un celu la RAM física se satura y el navegador mata la pestaña (pantalla
      // en blanco), tal como reporta el user. Bajando cuántos mapblocks retiene
      // el cliente y la distancia de visión, el pico de RAM baja del umbral de
      // crash → se puede jugar solo desde el navegador. NO afecta al APK (usa el
      // motor nativo, no este loader) ni a escritorio (este código solo corre en
      // el iframe del navegador; el clamp se gatilla por User-Agent móvil).
      // Se toma el MÍNIMO con lo ya configurado (host/join/prefs), nunca sube.
      try {
        const ua = navigator.userAgent || '';
        const isMobileWeb = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
        if (isMobileWeb) {
          const clampInt = (k: string, cap: number) => {
            const cur = parseInt(conf[k] ?? '', 10);
            conf[k] = String(isNaN(cur) ? cap : Math.min(cur, cap));
          };
          clampInt('client_mapblock_limit', 3000);      // 8000 → 3000: mucho menos mapa en RAM
          clampInt('viewing_range', 96);                // menos área cargada
          clampInt('max_block_generate_distance', 4);
          clampInt('max_block_send_distance', 5);
          // Liberar bloques no usados rápido (default 600s) para que el pico no se acumule.
          conf['client_unload_unused_data_timeout'] = '60';
        }
      } catch (e) { /* UA rara: sin clamp */ }

      // [PC] Generación de terreno (EmergeManager) — usar el pool de hilos y
      // alimentarlo. El default es 1 hilo generando TODO el mundo (por eso la
      // "sensación de lag" con FPS estables: el mapgen pesado de VoxeLibre no
      // llega). Subimos los hilos de forma ADAPTATIVA por CPU, dejando margen
      // para el render + el hilo principal, y AGRANDAMOS la cola de generación
      // (default 128, y en móvil el motor la baja a 16 → los hilos se quedan sin
      // trabajo). En móvil se acota más para no empeorar la memoria.
      try {
        const cores = (navigator.hardwareConcurrency as number) || 4;
        const isMobileWeb = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
        // cores-1 en escritorio (deja 1 para el main), cores-2 en móvil; topes
        // conservadores: el mapgen rinde poco más allá de eso y no queremos
        // agotar el pool (20) ni disparar memoria.
        const threads = isMobileWeb
          ? Math.max(1, Math.min(cores - 2, 2))
          : Math.max(1, Math.min(cores - 1, 4));
        conf['num_emerge_threads'] = String(threads);
        conf['emergequeue_limit_generate'] = String(isMobileWeb ? 48 : 256);
        conf['emergequeue_limit_total'] = String(isMobileWeb ? 512 : 2048);
      } catch (e) { /* sin hardwareConcurrency: default 1 hilo */ }

      // [PC] Controles táctiles: el motor web ahora se compila con ENABLE_TOUCH,
      // pero crea el GUI táctil SOLO si este setting está prendido (ver
      // clientlauncher.cpp). Lo prendemos en navegador móvil → en escritorio NO
      // aparecen los botones.
      try {
        const ua = navigator.userAgent || '';
        // [PC] `?touch=1` (o ?pctouch=1) FUERZA los táctiles en cualquier
        // dispositivo — sirve para probar/depurar los controles móviles desde
        // escritorio (la consola del navegador muestra los logs del motor).
        let force = false;
        try {
          const s =
            (window.top && (window.top as any).location && (window.top as any).location.search) ||
            window.location.search || '';
          force = /[?&](touch|pctouch)=1/.test(s);
        } catch (e) { /* cross-origin: ignorar */ }
        conf['pc_touch_controls'] =
          (force || /Android|iPhone|iPad|iPod|Mobile/i.test(ua)) ? 'true' : 'false';
      } catch (e) { conf['pc_touch_controls'] = 'false'; }

      setMinetestConf(conf);

      // Initialize sound
      if (window.emloop_init_sound) window.emloop_init_sound();

      // Initialize emsocket after setting proxy and VPN
      if (window.emsocket_init) {
        window.emsocket_init();
        minetestConsole.print("emsocket initialized");
      }

      // Set up network - do this before initializing emsocket
      setProxy(gameOptions.proxy);
      
      // Handle game mode specific settings and VPN setup
      // Do this before emsocket_init
      if (gameOptions.mode === 'join') {
        if (!gameOptions.joinCode) {
          throw new Error('RuntimeScreen: Join code is required');
        }
        setVpn(null, gameOptions.joinCode);
      }
      else if (gameOptions.mode === 'host') {
        // PiolaCraft MP (modelo "LAN desde el arranque"): el mundo YA levanta como
        // server en la VPN (local_server, puerto 30000), pero NO se anuncia a nadie.
        // Guardamos el clientCode; el botón "Abrir LAN" del juego lo publica después
        // SIN recargar (ver bridge MP más abajo, acción open_room). Si el proxy no
        // responde, seguimos jugando normal (solo sin LAN esa sesión).
        // El proxy a veces no contesta al primer intento (p.ej. justo tras el
        // relaunch del iframe la conexión anterior sigue colgada) — reintentar 1 vez.
        for (let intento = 1; intento <= 2; intento++) {
          try {
            const [cmd, serverCode, clientCode] = await queryProxy(`MAKEVPN ${gameOptions.gameId}`, gameOptions.proxy);
            if (cmd != 'NEWVPN') {
              throw new Error('Invalid response from proxy');
            }
            console.log('serverCode', serverCode);
            console.log('clientCode', clientCode);
            setVpn(serverCode, clientCode);
            (window as any).__pcHostClientCode = clientCode;
            (window as any).__pcHostGameId = gameOptions.gameId;
            (window as any).__pcHostRealmId = gameOptions.realmId || '';
            (window as any).__pcHostWorld = gameOptions.realmId || chosenWorldRef.current || gameOptions.worldName || 'Mundo';
            break;
          } catch (e) {
            if (intento === 1) {
              minetestConsole.printErr('PiolaCraft MP: MAKEVPN falló, reintentando...');
            } else {
              minetestConsole.printErr('PiolaCraft MP: MAKEVPN falló, LAN deshabilitado esta sesión (se juega igual, en solo).');
            }
          }
        }
      }

      // Set up minetest args
      const { minetestArgs } = gameOptions;
      minetestArgs.clear();

      // NOTE: With --go the server seems to load too slowly for the client to connect,
      // at least for big games like voxelibre

      // Note: Share an object like {"gameId": "minetest_game", "clientCode": "..."} to the clients

      if (gameOptions.mode === 'join') {
        minetestArgs.go = true;
        minetestArgs.gameid = gameOptions.gameId;
        minetestArgs.address = '172.16.0.1';
        minetestArgs.port = 30000;
        minetestArgs.name = gameOptions.playerName || 'Player' + Math.random().toString(36).substring(2, 7);
        // Contraseña ESTABLE por jugador (no aleatoria). El server registra al jugador con
        // su contraseña en el primer join; si en el próximo join mandamos otra aleatoria,
        // rechaza con "Invalid password". Derivándola del nombre es la misma siempre, así
        // el mismo jugador puede re-entrar al mundo de un amigo. El acceso real lo controla
        // el VPN (clientCode), no la contraseña.
        minetestArgs.password = gameOptions.password || ('pc-' + (gameOptions.playerName || 'player'));
      }
      else if (gameOptions.mode === 'host' && gameOptions.realmId) {
        // Realm host: booteamos DIRECTO al mundo del realm (--go), sin pasar por el
        // menú. El mundo ya lo restauramos arriba en /minetest/worlds/<realmId> (o es
        // nuevo). Se hostea como server en la VPN; el auto-anuncio (post-launch) toma
        // el lock del realm en el hub. Password estable (auth compartida entre hosts).
        minetestArgs.go = true;
        minetestArgs.gameid = gameOptions.gameId;
        minetestArgs.worldname = gameOptions.realmId;
        minetestArgs.name = gameOptions.playerName || 'Host';
        minetestArgs.password = 'pc-' + (gameOptions.playerName || 'player');
        minetestArgs.port = 30000;
      }
      else if (gameOptions.mode === 'host') {
        // Booteamos al MENÚ de PiolaCraft (SIN --go): el usuario elige/crea el mundo
        // con Singleplayer. El VPN ya quedó armado arriba (MAKEVPN + setVpn), y el
        // menú (dlg_pc_worlds/newworld) lanza el mundo como SERVER en el puerto 30000
        // → los amigos se pueden unir. "Abrir LAN" publica la sala (window.__pcHost*).
        minetestArgs.gameid = gameOptions.gameId;
        minetestArgs.name = gameOptions.playerName || 'Host';
        minetestArgs.port = 30000;
      }

      if (minetestArgs.go && window.irrlicht_force_pointerlock) {
        window.irrlicht_force_pointerlock();
      }

      console.log("GAME OPTIONS", gameOptions);

      // Launch the game. Mi motor: emloop_invoke_main. Motor de Gemini: callMain.
      if (window.emloop_invoke_main) {
        const fullArgs = ['./minetest', ...minetestArgs.toArray()];
        minetestConsole.print("Starting (emloop): " + fullArgs.join(' '));
        const [argc, argv] = makeArgv(fullArgs);
        window.emloop_invoke_main(argc, argv);
        window.emloop_request_animation_frame?.();
        // Recién acá el FS es seguro para el puente MP: tocar el FS ANTES de
        // invoke_main (con el proxying de WASMFS a medio armar) llama abort()
        // ("Fatal error in FS.readFile") y mata el boot. El poll y el writeFile
        // del puente esperan este flag.
        (window as any).__pcGameRunning = true;
        onGameStatus('running');
      } else if (window.Module && window.Module.callMain) {
        const fullArgs = ['./minetest', ...minetestArgs.toArray()];
        minetestConsole.print("Starting (callMain): " + fullArgs.join(' '));
        window.Module.callMain(fullArgs);
        window.emloop_request_animation_frame?.();
        (window as any).__pcGameRunning = true;
        onGameStatus('running');
      }

      // Realm: auto-anunciar la sala apenas arranca (no hay "Abrir LAN" manual — el
      // realm se prende solo). El hub, al recibir PIOLA_HOSTING con realmId, toma el
      // lock del realm (open) con este clientCode.
      // Anunciar SIEMPRE (aunque MAKEVPN haya fallado y no haya clientCode): el hub
      // necesita tomar el lock de host en el backend o el PUT del mundo da 403 y la
      // partida no se guarda. Con código vacío el realm queda "prendido sin LAN".
      if (gameOptions.mode === 'host' && gameOptions.realmId) {
        const code = (window as any).__pcHostClientCode || '';
        (window as any).__pcAdvertised = true;
        window.parent.postMessage({
          source: 'piolacraft-game', type: 'PIOLA_HOSTING',
          gameId: gameOptions.gameId, clientCode: code,
          worldName: gameOptions.realmId, realmId: gameOptions.realmId,
        }, '*');
        minetestConsole.print('Realm: sala anunciada al hub (' + gameOptions.realmId + (code ? ')' : ', SIN LAN)'));
      }
    } catch (error) {
      minetestConsole.printErr(`Error launching game: ${error}`);
      setIsLoading(false);
      onGameStatus('failed');
    } finally {
      setIsLoading(false);
    }
  });

  // Setup the global functions needed by the WASM module
  useLayoutEffect(() => {
    // Define the emloop_ready function that will be called by the WASM module
    window.emloop_ready = () => {
      minetestConsole.print("emloop_ready called. Setting up functions...");

      try {
        // Setup cwrapped functions - copied from original launcher.js
        window.emloop_pause = window.cwrap("emloop_pause", null, []);
        window.emloop_unpause = window.cwrap("emloop_unpause", null, []);
        window.emloop_init_sound = window.cwrap("emloop_init_sound", null, []);
        window.emloop_install_pack = window.cwrap("emloop_install_pack", null, ["number", "number", "number"]);
        // Estas dos solo existen en mi motor (emloop). El de Gemini no las tiene,
        // así que solo las envolvemos si el export crudo existe (cwrap de una
        // función ausente puede tirar y romper el setup).
        if (window.Module && window.Module._emloop_invoke_main)
          window.emloop_invoke_main = window.cwrap("emloop_invoke_main", null, ["number", "number"]);
        if (window.Module && window.Module._emloop_set_minetest_conf)
          window.emloop_set_minetest_conf = window.cwrap("emloop_set_minetest_conf", null, ["number"]);
        window.irrlicht_want_pointerlock = window.cwrap("irrlicht_want_pointerlock", "number", []);
        window.irrlicht_force_pointerlock = window.cwrap("irrlicht_force_pointerlock", null, []);
        window.irrlicht_resize = window.cwrap("irrlicht_resize", null, ["number", "number"]);
        window.emsocket_init = window.cwrap("emsocket_init", null, []);
        window.emsocket_set_proxy = window.cwrap("emsocket_set_proxy", null, ["number"]);
        window.emsocket_set_vpn = window.cwrap("emsocket_set_vpn", null, ["number"]);

        minetestConsole.print("Successfully wrapped Emscripten functions");

        launchGameRef.current?.();
      } catch (err) {
        minetestConsole.printErr(`Error setting up game: ${err}`);
        setIsLoading(false);
      }
    };

    // Define emloop_request_animation_frame function.
    // NO-OP: emscripten_set_main_loop ya maneja el scheduling. Llamar
    // emloop_pause/unpause desde acá corrompe la máquina de estados de Asyncify.
    window.emloop_request_animation_frame = () => {
      // no-op
    };

    return () => {
      // Cleanup global functions
      delete window.emloop_ready;
      delete window.emloop_request_animation_frame;
    };
  }, []);

  // ── PiolaCraft Multiplayer: puente de datos (hub ↔ juego) ──────────────────
  // El hub (parent, con tu login) nos manda las listas por postMessage; las
  // escribimos en el FS para que el menú NATIVO las lea. Y detectamos las
  // acciones del menú (escritas en minetest.conf) para reenviarlas al hub.
  useEffect(() => {
    // El runtime está listo cuando stackSave es función (si no, tocar el FS
    // durante la init tira "stackSave is not a function" y puede romper el WASM).
    // Además esperamos __pcGameRunning (seteado tras emloop_invoke_main): tocar
    // el FS en la ventana entre emloop_ready y el arranque real ABORTA el módulo
    // ("Fatal error in FS.readFile") — era una carrera con la restauración del
    // IndexedDB que se perdía cuanto más archivos persistidos hubiera.
    const runtimeReady = () => {
      const M = (window as any).Module;
      return !!(M && M.FS && typeof M.stackSave === 'function' && (window as any).__pcGameRunning);
    };

    // 1) Recibir listas del hub → escribir pc_mp_data.json. Los pushes que
    // llegan ANTES de que el runtime esté listo (todo el boot, ~15-20s) NO se
    // descartan: se guarda el último payload y lo escribe el poll de 1s apenas
    // el juego arranca. Antes se tiraban → el menú Multiplayer abría con el
    // archivo viejo/ausente y los realms "no aparecían" hasta que un push
    // casual (cada 10s) o una acción (crear realm) refrescaba el archivo.
    let pendingMpData: any = null;
    let lastMpCounts = '';
    const writeMpData = (data: any) => {
      const FS = (window as any).Module.FS;
      // OJO: el FS.writeFile de este build WASMFS APPENDEA en vez de truncar
      // → el archivo quedaba como {...}{...}{...} y el parse_json del menú
      // fallaba desde el segundo push (los realms nunca se actualizaban).
      // Borrar antes de escribir garantiza un único documento JSON.
      try { FS.unlink('/minetest/pc_mp_data.json'); } catch (e) { /* no existía */ }
      FS.writeFile('/minetest/pc_mp_data.json', JSON.stringify(data || {}));
      const c = (data?.realms || []).length + ' realms, ' +
        (data?.friendsList || []).length + ' amigos, ' +
        (data?.invitations || []).length + ' invitaciones';
      if (c !== lastMpCounts) {
        lastMpCounts = c;
        minetestConsole.print('[MP diag] pc_mp_data escrito: ' + c);
      }
    };
    const onHubMsg = (ev: MessageEvent) => {
      const d = ev.data;
      if (!d || typeof d !== 'object' || d.source !== 'piolacraft-hub') return;
      if (d.type === 'PIOLA_MP_DATA') {
        if (!runtimeReady()) { pendingMpData = d.data || {}; return; }
        try {
          writeMpData(d.data);
          pendingMpData = null;
        } catch (e) { /* silencioso: el hub reintenta cada 10s */ }
      }
    };
    window.addEventListener('message', onHubMsg);

    // 2) Detectar acciones del menú nativo (escritas en minetest.conf) → reenviar.
    // El menú escribe pc_mp_action=<verbo:args> y bumpea pc_mp_action_n; ídem
    // pc_mp_open (abrir panel) y pc_mp_refresh (Actualizar).
    let lastMpOpenCount = -1, lastMpActionCount = -1, lastMpRefreshCount = -1;
    let lastSkinUploadCount = -1;
    let hasInit = false;
    let diagCount = 0;
    // Foto de los keymap_* de la conf: para detectar re-mapeos del diálogo
    // "Cambiar teclas" del engine y persistirlos (localStorage pcKeymap).
    let lastKeymap: Record<string, string> | null = null;
    const post = (msg: any) => { try { window.parent.postMessage({ source: 'piolacraft-game', ...msg }, '*'); } catch (e) { /* */ } };

    // ── Cargar skin (botón del menú de skins) ────────────────────────────
    // El menú bumpea pc_skin_upload en la conf; acá abrimos el selector de
    // archivos. Llega vía onFileChange (inmediato tras el click del usuario),
    // así que seguimos dentro de la "activación transitoria" del navegador y
    // el explorador abre siempre (antes dependía del poll de 1s y era una
    // lotería). La skin va a /minetest/pc_custom_skin_<slot>.png (slot libre
    // 1..4, si no pisa el 4) + vista previa del personaje armada con canvas.
    const buildSkinPreview = (file: File): Promise<Uint8Array | null> =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          try {
            const legacy = img.height === 32; // skin vieja 64x32: espejar lado izq.
            const c = document.createElement('canvas');
            c.width = 16; c.height = 32;
            const g = c.getContext('2d');
            if (!g) { resolve(null); return; }
            g.imageSmoothingEnabled = false;
            g.drawImage(img, 8, 8, 8, 8, 4, 0, 8, 8);    // cabeza
            g.drawImage(img, 40, 8, 8, 8, 4, 0, 8, 8);   // capa de la cabeza
            g.drawImage(img, 20, 20, 8, 12, 4, 8, 8, 12); // cuerpo
            g.drawImage(img, 44, 20, 4, 12, 0, 8, 4, 12); // brazo derecho
            if (legacy) {
              g.save(); g.translate(16, 0); g.scale(-1, 1);
              g.drawImage(img, 44, 20, 4, 12, 0, 8, 4, 12); // brazo izq (espejo)
              g.restore();
            } else {
              g.drawImage(img, 36, 52, 4, 12, 12, 8, 4, 12); // brazo izq
            }
            g.drawImage(img, 4, 20, 4, 12, 4, 20, 4, 12); // pierna derecha
            if (legacy) {
              g.save(); g.translate(16, 0); g.scale(-1, 1);
              g.drawImage(img, 4, 20, 4, 12, 4, 20, 4, 12); // pierna izq (espejo)
              g.restore();
            } else {
              g.drawImage(img, 20, 52, 4, 12, 8, 20, 4, 12); // pierna izq
            }
            const c2 = document.createElement('canvas');
            c2.width = 128; c2.height = 256;
            const g2 = c2.getContext('2d');
            if (!g2) { resolve(null); return; }
            g2.imageSmoothingEnabled = false;
            g2.drawImage(c, 0, 0, 128, 256);
            c2.toBlob(async (b) => {
              if (!b) { resolve(null); return; }
              resolve(new Uint8Array(await b.arrayBuffer()));
            }, 'image/png');
          } catch (e) { resolve(null); }
        };
        img.onerror = () => resolve(null);
        img.src = URL.createObjectURL(file);
      });

    const openSkinPicker = () => {
      try {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/png';
        input.onchange = async () => {
          const file = input.files && input.files[0];
          if (!file) return;
          const buf = new Uint8Array(await file.arrayBuffer());
          if (!(buf.length > 8 && buf[0] === 0x89 && buf[1] === 0x50)) {
            minetestConsole.printErr('Skin: el archivo no es un PNG.');
            return;
          }
          const FS = (window as any).Module?.FS;
          if (!FS) return;
          let slot = 4;
          for (let i = 1; i <= 4; i++) {
            try { FS.stat('/minetest/pc_custom_skin_' + i + '.png'); }
            catch (e) { slot = i; break; }
          }
          const path = '/minetest/pc_custom_skin_' + slot + '.png';
          FS.writeFile(path, buf);
          try { storageManager?.fileChanged(path); } catch (e) { /* */ }
          try {
            const prev = await buildSkinPreview(file);
            if (prev) {
              const ppath = '/minetest/pc_custom_skin_preview_' + slot + '.png';
              FS.writeFile(ppath, prev);
              try { storageManager?.fileChanged(ppath); } catch (e) { /* */ }
            }
          } catch (e) { /* sin preview: el menú muestra la textura cruda */ }
          minetestConsole.print('Skin cargada en el espacio ' + slot +
            ' — toca "Actualizar" en el menu de skins para verla.');
        };
        input.click();
      } catch (e) {
        minetestConsole.printErr('Skin: no se pudo abrir el selector de archivos.');
      }
    };
    const poll = () => {
      if (!runtimeReady()) return;
      const FS = (window as any).Module.FS;
      // Push del hub que llegó durante el boot: escribirlo ahora que se puede.
      if (pendingMpData) {
        try { writeMpData(pendingMpData); } catch (e) { /* reintenta el próximo tick */ }
        pendingMpData = null;
      }
      let confBuf: string | null = null;
      try { confBuf = FS.readFile('/minetest/minetest.conf', { encoding: 'utf8' }); } catch (e) { confBuf = null; }
      diagCount++;
      if (confBuf == null) {
        if (diagCount % 15 === 0) minetestConsole.print('[MP diag] /minetest/minetest.conf NO legible aún');
        return;
      }
      if (diagCount % 15 === 0) {
        const a = confBuf.match(/pc_mp_action_n\s*=\s*(\d+)/);
        minetestConsole.print('[MP diag] conf ' + confBuf.length + 'b, pc_mp_action_n=' + (a ? a[1] : 'ausente'));
      }
      // Re-mapeos de teclas ("Cambiar teclas" del engine): el engine los
      // persiste en SU conf, pero este loader la reescribe entera al boot.
      // Comparamos contra la foto anterior y guardamos los cambios en
      // pcKeymap (se re-aplican al armar la conf del próximo arranque).
      {
        const kmNow: Record<string, string> = {};
        const re = /^(keymap_[a-z0-9_]+|doubletap_jump|aux1_descends|autojump)[ \t]*=[ \t]*(.*)$/gm;
        let m: RegExpExecArray | null;
        while ((m = re.exec(confBuf)) !== null) {
          kmNow[m[1]] = m[2].trim();
        }
        if (!lastKeymap) {
          lastKeymap = kmNow;
        } else {
          // Cambió el valor, o apareció una tecla nueva (el diálogo del
          // engine vuelca teclas que el conf del loader no traía).
          const diff = Object.keys(kmNow).filter((k) => lastKeymap![k] !== kmNow[k]);
          if (diff.length > 0) {
            try {
              const stored = JSON.parse(localStorage.getItem('pcKeymap') || '{}');
              for (const k of diff) stored[k] = kmNow[k];
              localStorage.setItem('pcKeymap', JSON.stringify(stored));
              minetestConsole.print('Controles: teclas guardadas (' + diff.join(', ') + ')');
            } catch (e) { /* localStorage lleno/bloqueado: quedan por sesión */ }
          }
          lastKeymap = kmNow;
        }
      }
      // Contadores (ausente = 0). En la PRIMERA lectura fijamos la baseline sin
      // disparar (para no re-disparar valores persistidos de una sesión previa);
      // después disparamos cuando suben.
      const getN = (re: RegExp) => { const m = confBuf!.match(re); return m ? parseInt(m[1]) : 0; };
      const nOpen = getN(/pc_mp_open\s*=\s*(\d+)/);
      const nAct = getN(/pc_mp_action_n\s*=\s*(\d+)/);
      const nRef = getN(/pc_mp_refresh\s*=\s*(\d+)/);
      const nSkin = getN(/pc_skin_upload\s*=\s*(\d+)/);
      if (!hasInit) {
        lastMpOpenCount = nOpen; lastMpActionCount = nAct; lastMpRefreshCount = nRef;
        lastSkinUploadCount = nSkin;
        hasInit = true;
        return;
      }
      if (nSkin > lastSkinUploadCount) { lastSkinUploadCount = nSkin; openSkinPicker(); }
      if (nOpen > lastMpOpenCount) { lastMpOpenCount = nOpen; post({ type: 'PIOLA_OPEN_MP' }); }
      if (nAct > lastMpActionCount) {
        lastMpActionCount = nAct;
        const am = confBuf.match(/pc_mp_action\s*=\s*(.+)/);
        const payload = am ? am[1].trim() : '';
        const verb = payload.indexOf(':') === -1 ? payload : payload.slice(0, payload.indexOf(':'));
        if (verb === 'open_room') {
          // "Abrir LAN" = switch. El mundo ya es server; solo publicamos/despublicamos
          // la sala en el hub con el clientCode que guardamos al arrancar. SIN recargar.
          const code = (window as any).__pcHostClientCode;
          if (!code) {
            minetestConsole.printErr('PiolaCraft MP: no hay LAN para este mundo (proxy no respondió al arrancar).');
          } else if (!(window as any).__pcAdvertised) {
            (window as any).__pcAdvertised = true;
            post({ type: 'PIOLA_HOSTING', gameId: (window as any).__pcHostGameId, clientCode: code, worldName: (window as any).__pcHostWorld });
            minetestConsole.print('PiolaCraft MP: LAN ABIERTA (sala publicada a tus amigos).');
          } else {
            (window as any).__pcAdvertised = false;
            post({ type: 'PIOLA_UNHOSTING' });
            minetestConsole.print('PiolaCraft MP: LAN CERRADA (sala despublicada).');
          }
        } else if (verb === 'display') {
          // Opciones de PANTALLA del menú del juego: se aplican acá (loader) y
          // se guardan en localStorage — NO van al hub.
          const kv = payload.slice(payload.indexOf(':') + 1);
          const eq = kv.indexOf('=');
          const k = eq === -1 ? kv : kv.slice(0, eq);
          const v = eq === -1 ? '' : kv.slice(eq + 1);
          try {
            const disp = JSON.parse(localStorage.getItem('pcDisplay') || '{}');
            if (k === 'res') { setResolution(v); disp.res = v; }
            else if (k === 'aspect') { setAspectRatio(v); disp.aspect = v; }
            else if (k === 'console') { setShowConsole(v === '1'); disp.console = v; }
            localStorage.setItem('pcDisplay', JSON.stringify(disp));
          } catch (e) { /* */ }
          minetestConsole.print('Opciones: pantalla -> ' + kv);
        } else if (verb === 'pref') {
          // Preferencia del juego (font_size, viewing_range, sound_volume...):
          // persistirla — el loader reescribe minetest.conf entero en cada boot,
          // así que sin esto todo ajuste se perdería al recargar la página.
          const kv = payload.slice(payload.indexOf(':') + 1);
          const eq = kv.indexOf('=');
          if (eq > 0) {
            try {
              const prefs = JSON.parse(localStorage.getItem('pcPrefs') || '{}');
              prefs[kv.slice(0, eq)] = kv.slice(eq + 1);
              localStorage.setItem('pcPrefs', JSON.stringify(prefs));
            } catch (e) { /* */ }
          }
          minetestConsole.print('Opciones: pref -> ' + kv);
        } else if (payload === 'shot') {
          // Camarita del menú de pausa: el juego guardó la captura en el FS
          // wasm. Se la mandamos al hub para que la deje en la Galería.
          // (Antes se bajaba como descarga del navegador.)
          setTimeout(() => {
            try {
              const FS = (window as any).Module?.FS;
              if (!FS) return;
              const dir = '/minetest/screenshots';
              const files = (FS.readdir(dir) as string[])
                .filter((f) => f.endsWith('.png'));
              if (!files.length) { minetestConsole.print('Captura: no encontrada'); return; }
              files.sort();
              const name = files[files.length - 1];
              const data = FS.readFile(dir + '/' + name);
              const blob = new Blob([data], { type: 'image/png' });

              // El PNG crudo de pantalla completa pesa varios MB y la Galería
              // guarda en localStorage (~5MB en total) → hay que recomprimir.
              // Se reescala a 1600px de ancho como mucho y se pasa a webp.
              const img = new Image();
              const src = URL.createObjectURL(blob);
              img.onload = () => {
                try {
                  const MAXW = 1600;
                  const scale = Math.min(1, MAXW / img.width);
                  const cv = document.createElement('canvas');
                  cv.width = Math.round(img.width * scale);
                  cv.height = Math.round(img.height * scale);
                  cv.getContext('2d')!.drawImage(img, 0, 0, cv.width, cv.height);
                  const dataUrl = cv.toDataURL('image/webp', 0.85);
                  post({ type: 'PIOLA_SHOT', name, dataUrl, w: cv.width, h: cv.height });
                  minetestConsole.print('Captura enviada a la Galería: ' + name);
                } catch (e) {
                  minetestConsole.print('Captura: error al comprimir');
                } finally {
                  URL.revokeObjectURL(src);
                  // Borrarla del FS: ya viajó al hub y si no, se acumulan en
                  // memoria del wasm (y la próxima captura sería la vieja si
                  // el nombre coincidiera).
                  try { FS.unlink(dir + '/' + name); } catch (e) { /* */ }
                }
              };
              img.onerror = () => { URL.revokeObjectURL(src); minetestConsole.print('Captura: PNG ilegible'); };
              img.src = src;
            } catch (e) {
              minetestConsole.print('Captura: error al leer el FS');
            }
          }, 500);
        } else {
          post({ type: 'PIOLA_MP_ACTION', action: payload });
          minetestConsole.print('PiolaCraft MP: acción -> ' + payload);
        }
      }
      if (nRef > lastMpRefreshCount) { lastMpRefreshCount = nRef; post({ type: 'PIOLA_MP_REFRESH' }); }
    };
    // Exponer para llamarlo desde onFileChange (trigger inmediato al escribir la conf).
    (window as any).__pcCheckMpConf = poll;
    const id = setInterval(poll, 1000);
    return () => { window.removeEventListener('message', onHubMsg); clearInterval(id); delete (window as any).__pcCheckMpConf; };
  }, []);

  // Micrófono ("driver universal" del navegador): getUserMedia toma el mic
  // por defecto del dispositivo (PC, teléfono, headset...). Cuando el juego
  // activa pc_mic_enabled (botón del menú principal), se pide permiso y se
  // arranca un medidor de nivel (RMS del AnalyserNode) que escribe 0-100 en
  // /minetest/pc_mic_level; el engine lo espeja a la conf compartida para el
  // medidor del menú Volumen y el mic sobre la cabeza (mod pc_voice).
  useEffect(() => {
    let stream: MediaStream | null = null;
    let audioCtx: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    let levelTimer: number | null = null;
    let starting = false;
    let gain = 1.0;

    const writeLevel = (lvl: number) => {
      if (!(window as any).__pcGameRunning) return;
      try {
        (window as any).Module?.FS?.writeFile('/minetest/pc_mic_level',
          String(Math.max(0, Math.min(100, Math.round(lvl)))));
      } catch (e) { /* FS todavía no está */ }
    };

    const stopMic = () => {
      if (levelTimer !== null) { window.clearInterval(levelTimer); levelTimer = null; }
      if (stream) { stream.getTracks().forEach(t => t.stop()); stream = null; }
      if (audioCtx) { audioCtx.close().catch(() => { /* */ }); audioCtx = null; }
      analyser = null;
      writeLevel(0);
    };

    const startMic = async () => {
      if (starting || stream) return;
      starting = true;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
        });
        audioCtx = new AudioContext();
        const src = audioCtx.createMediaStreamSource(stream);
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        src.connect(analyser);
        const buf = new Uint8Array(analyser.fftSize);
        levelTimer = window.setInterval(() => {
          if (!analyser) return;
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) {
            const v = (buf[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / buf.length);
          writeLevel(rms * 350 * gain);
        }, 120);
        console.log('PiolaCraft mic: capturando (permiso concedido)');
      } catch (e) {
        console.warn('PiolaCraft mic: sin permiso o sin dispositivo', e);
        stopMic();
      }
      starting = false;
    };

    // Observa la conf del juego: pc_mic_enabled (botón del menú) enciende y
    // apaga la captura; pc_voice_volume es la ganancia del medidor.
    const poll = window.setInterval(() => {
      // Mismo guard que el puente MP: tocar el FS en la ventana entre
      // emloop_ready y emloop_invoke_main (WASMFS a medio armar) llama
      // abort() ("Fatal error in FS.readFile") aunque haya try/catch.
      const M = (window as any).Module;
      if (!M?.FS || typeof M.stackSave !== 'function' ||
          !(window as any).__pcGameRunning) return;
      const FS = M.FS;
      let conf = '';
      try { conf = new TextDecoder().decode(FS.readFile('/minetest/minetest.conf')); }
      catch (e) { return; }
      const on = /pc_mic_enabled\s*=\s*true/.test(conf);
      const g = conf.match(/pc_voice_volume\s*=\s*([\d.]+)/);
      gain = g ? Math.max(0, Math.min(1, parseFloat(g[1]))) : 1.0;
      if (on && !stream && !starting) startMic();
      else if (!on && stream) stopMic();
    }, 700);

    return () => { window.clearInterval(poll); stopMic(); };
  }, []);

  // Initialize the WASM module
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    // Cache-busting del engine: sin esto el navegador puede servir un
    // minetest.js/.wasm VIEJO de su caché HTTP después de un deploy (nos
    // pasó: loader nuevo + motor viejo = bugs "imposibles"). En localhost
    // re-bajar 22MB por sesión es gratis.
    const pcEngineV = Date.now().toString(36);
    const modulePath = `minetest/minetest.js?v=${pcEngineV}`;

    // Set up Module configuration for Emscripten
    window.Module = {
      // Motor emloop (World A): main() arranca solo y LLAMA a emloop_ready (que
      // hace el setup + lanza el juego). Por eso noInitialRun debe ser false.
      noInitialRun: false,
      preRun: [],
      postRun: [],
      print: minetestConsole.print,
      printErr: minetestConsole.printErr,
      canvas: canvas,
      onAbort: () => {
        minetestConsole.printErr('Fatal error: Emscripten module aborted');
        onGameStatus('failed');
      },
      totalDependencies: 0,
      monitorRunDependencies: (left: number) => {
        const deps = Math.max(window.Module.totalDependencies, left);
        window.Module.totalDependencies = deps;
        const progress = (deps - left) / deps;
        minetestConsole.print(`Loading progress: ${Math.round(progress * 100)}%`);
      },
      setStatus: (text: string) => {
        if (text) minetestConsole.print('[wasm module status] ' + text);
      },
      onRuntimeInitialized: () => {
        // Motor emloop (World A): NO disparamos emloop_ready acá. El main() del
        // motor lo llama solo tras su setup interno. Dispararlo a mano acá (antes
        // de que main() corra) rompería el handoff de Asyncify.
        minetestConsole.print('Runtime initialized (esperando emloop_ready desde main)...');
      },
      // Add handlers for file operations to sync with IndexedDB
      onFileChange: (path: string) => {
        // Normalize the path
        if (storageManager) {
          storageManager.fileChanged(path);
        }
        // PiolaCraft MP: el engine escribió un archivo. Si es la conf (donde el
        // menú escribe pc_mp_action), chequear ya mismo (no esperar el poll).
        if (typeof path === 'string' && path.indexOf('minetest.conf') !== -1) {
          minetestConsole.print('[MP diag] onFileChange conf -> ' + path);
          try { (window as any).__pcCheckMpConf?.(); } catch (e) { /* */ }
        }
      },
      onFileDelete: (path: string) => {
        if (storageManager) {
          storageManager.fileDeleted(path);
        }
      }
    };

    // Add worker injection script for proper thread communication
    const workerInject = `
      Module['print'] = (text) => {
        postMessage({cmd: 'callHandler', handler: 'print', args: [text], threadId: Module['_pthread_self']()});
      };
      Module['printErr'] = (text) => {
        postMessage({cmd: 'callHandler', handler: 'printErr', args: [text], threadId: Module['_pthread_self']()});
      };
      importScripts(location.origin + '/minetest/minetest.js?v=${pcEngineV}');
    `;
    window.Module['mainScriptUrlOrBlob'] = new Blob([workerInject], { type: "text/javascript" });
    window.Module['onFullScreen'] = () => { fixGeometry(); };
    window.Module['locateFile'] = (path: string, prefix: string) => {
      if (path.endsWith('.wasm')) return 'minetest/' + path + '?v=' + pcEngineV;
      if (path.endsWith('.worker.js')) return 'minetest/' + path + '?v=' + pcEngineV;
      return prefix + path;
    };

    // Function to load the script
    const loadScript = () => {
      minetestConsole.print(`Loading WebAssembly module from ${modulePath}...`);
      const script = document.createElement('script');
      script.src = modulePath;
      script.async = true;
      script.onerror = () => {
        minetestConsole.printErr(`Error loading WebAssembly module from ${modulePath}`);
        onGameStatus('failed');
      };
      script.onload = () => {
        minetestConsole.print('WebAssembly module loaded successfully');
      };
      document.body.appendChild(script);
    };

    // Load the module
    loadScript();

    // Set up resize handling
    const handleResize = () => fixGeometry();
    window.addEventListener('resize', handleResize);

    // PiolaCraft: el engine fija su propio tamaño de canvas al arrancar
    // ("SETTING CANVAS SIZE") y pisa la geometría — re-aplicarla varias
    // veces después del boot para que el letterbox quede bien en ventana.
    // Las dos primeras son TEMPRANAS: sin ellas el menú arrancaba en una
    // cajita 1024x600 centrada y recién se acomodaba a los ~4 segundos.
    const refix0 = window.setTimeout(handleResize, 500);
    const refix05 = window.setTimeout(handleResize, 1500);
    const refix1 = window.setTimeout(handleResize, 4000);
    const refix2 = window.setTimeout(handleResize, 10000);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.clearTimeout(refix0);
      window.clearTimeout(refix05);
      window.clearTimeout(refix1);
      window.clearTimeout(refix2);
    };
  }, [onGameStatus]);

  // Update geometry when resolution or aspect ratio changes
  useEffect(() => {
    fixGeometry();
  }, [resolution, aspectRatio]);

  useEffect(() => {
    if (storageManager) {
      storageManager.setMinetestConsole(minetestConsole);
    }
  }, [storageManager, minetestConsole]);

  // PiolaCraft: guardar el mundo a IndexedDB best-effort cuando la pestaña se oculta
  // o se va a descargar (cambio de pestaña, cerrar, o recarga del iframe al unirse).
  // Complementa el flush periódico (max-wait) para no perder los últimos cambios.
  useEffect(() => {
    if (!storageManager) return;
    const flush = () => { try { void storageManager.executeWorldSync(); } catch (e) { /* */ } };
    const onVis = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', flush);
    };
  }, [storageManager]);

  // PiolaCraft Realms: si somos el HOST de un realm, subimos el mundo compartido al
  // backend periódicamente (cada 45s) y al ocultar la pestaña. Así, cuando este host
  // se va y otro miembro lo prende, arranca desde la última versión guardada. El PUT
  // solo lo acepta el server si somos el host actual (lock tomado vía PIOLA_HOSTING).
  // OJO: minetestConsole cambia de identidad con CADA log (useMemo con messages) —
  // si va en las deps, el interval se resetea antes de los 45s y NUNCA sube. Ref.
  const mcRef = useRef(minetestConsole);
  mcRef.current = minetestConsole;
  useEffect(() => {
    if (!storageManager || gameOptions.mode !== 'host' || !gameOptions.realmId) return;
    const minetestConsole = mcRef.current;
    const rid = gameOptions.realmId;
    let busy = false;
    const uploadRealm = async () => {
      // NUNCA tocar el FS antes de emloop_invoke_main (aborta el módulo — el
      // boot puede demorar más de 10s si el proxy tarda, y la subida temprana
      // llegaba antes del arranque). Sin juego corriendo, no hay nada que subir.
      if (busy || !(window as any).__pcGameRunning) return;
      busy = true;
      try {
        // Sincronizar FS→IDB ANTES de exportar: sin esto el zip periódico salía
        // casi vacío (~800b) toda la partida — map.sqlite recién llegaba al IDB
        // con la subida final del exit. Si el navegador del host crasheaba, se
        // perdía toda la sesión. (Mismo sync que ya corre en pagehide.)
        try { await storageManager.executeWorldSync(); } catch (e) { /* best effort */ }
        const bytes = await storageManager.exportRealmWorld(rid);
        // Un zip de {} pesa ~22b: significa que el mundo aún no se persistió al
        // IndexedDB. NO subirlo — pisaría el mundo compartido con nada.
        if (bytes && bytes.length > 100) {
          const r = await fetch(`/api/pc/realms/${encodeURIComponent(rid)}/world`, {
            method: 'PUT', credentials: 'include',
            headers: { 'Content-Type': 'application/octet-stream' },
            body: bytes,
          });
          if (r.ok) minetestConsole.print(`Realm: mundo subido (${bytes.length}b)`);
          else minetestConsole.printErr(`Realm: PUT mundo HTTP ${r.status}`);
        }
        // Stats del realm (dias jugados/muertes, las escribe pc_realm_stats):
        // subirlas para que el hub las reparta a todos los miembros.
        try {
          const FS = (window as any).Module?.FS;
          if (FS) {
            // FS.readFile de un archivo INEXISTENTE aborta el módulo entero en este
            // WASMFS (no es un throw atrapable) — chequear existencia con FS.stat
            // primero (ese sí tira un error atrapable; mismo patrón que world.mt).
            const statsPath = '/minetest/worlds/' + rid + '/pc_stats.json';
            let hasStats = false;
            try { FS.stat(statsPath); hasStats = true; } catch (e) { /* aún no existe */ }
            const txt = hasStats ? FS.readFile(statsPath, { encoding: 'utf8' }) : '';
            if (txt) {
              await fetch(`/api/pc/realms/${encodeURIComponent(rid)}/stats`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ stats: JSON.parse(txt) }),
              });
            }
          }
        } catch (e) { /* sin stats todavía — normal en los primeros segundos */ }
      } catch (e) { minetestConsole.printErr('Realm: error subiendo mundo: ' + e); }
      finally { busy = false; }
    };
    // Primera subida temprana (el mundo recién creado ya queda respaldado aunque
    // el host se vaya enseguida); después, cada 30s. Sumado a visibilitychange,
    // pagehide y la subida final del exit: si el host se congela (pestaña de
    // fondo), crashea o cierra, el backend tiene una foto de a lo sumo ~30s
    // (y ~3s del mapa, por server_map_save_interval=3).
    const first = window.setTimeout(uploadRealm, 10_000);
    const iv = window.setInterval(uploadRealm, 30_000);
    const onVis = () => { if (document.visibilityState === 'hidden') void uploadRealm(); };
    document.addEventListener('visibilitychange', onVis);
    // Cierre/refresh de página: intento best-effort (el fetch puede sobrevivir
    // al unload; el zip del mundo suele exceder el límite de sendBeacon).
    const onHide = () => { void uploadRealm(); };
    window.addEventListener('pagehide', onHide);
    // Subida FINAL al salir del mundo: App.tsx la espera antes del reload, para
    // que lo construido en los últimos segundos llegue al backend (el shutdown
    // del server recién ahí escribe map.sqlite completo).
    (window as any).__pcRealmFinalUpload = async () => {
      for (let i = 0; i < 20 && busy; i++) await new Promise(r => setTimeout(r, 300));
      await uploadRealm();
    };
    return () => {
      window.clearTimeout(first); window.clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', onHide);
      delete (window as any).__pcRealmFinalUpload;
    };
  }, [storageManager, gameOptions.mode, gameOptions.realmId]);

  // ── Selector de mundos: acciones ────────────────────────────────────────────
  const playWorld = (name: string) => {
    const n = (name || '').trim() || 'Mundo';
    chosenWorldRef.current = n;
    setShowWorldPicker(false);
    setIsLoading(true);
    launchGameRef.current?.();
  };
  const deleteWorld = async (name: string) => {
    try { await storageManager?.deleteWorld?.(name); } catch (e) { /* */ }
    try { setWorldList((await storageManager?.getWorlds()) || []); } catch (e) { /* */ }
  };

  return (
    <div className="h-[100vh] w-[100vw] flex flex-col bg-black relative overflow-hidden">
      {/* El engranaje de ajustes se eliminó: resolución/aspecto/consola se
          manejan desde el menú Opciones del juego (verbos display:* del
          puente, que siguen activos más arriba). */}

      {/* Selector de mundos (modo host) */}
      {showWorldPicker && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4">
          <div className="w-full max-w-md p-4"
            style={{ background: '#c6c6c6', border: '2px solid #3f3f3f', boxShadow: 'inset 2px 2px 0 #ededed, inset -2px -2px 0 #8f8f8f, 0 10px 40px rgba(0,0,0,.7)' }}>
            <h2 className="text-center text-xl leading-tight" style={{ color: '#2b2b2b' }}>Tus mundos</h2>
            <p className="text-center text-xs mb-3" style={{ color: '#5e5e5e' }}>
              Elegí un mundo o creá uno nuevo. Al entrar, se abre para tus amigos.
            </p>
            <div className="overflow-y-auto mb-3" style={{ maxHeight: '14rem' }}>
              {worldList.length === 0 && (
                <p className="text-center text-sm py-4" style={{ color: '#5e5e5e' }}>
                  No tenés mundos todavía. Creá uno abajo.
                </p>
              )}
              {worldList.map((w) => (
                <div key={w} className="flex items-center gap-2 mb-1.5">
                  <button onClick={() => playWorld(w)} className="flex-1 text-left px-3 py-2 text-sm"
                    style={{ background: '#bdbdbd', borderTop: '2px solid #f3f3f3', borderLeft: '2px solid #f3f3f3', borderRight: '2px solid #5a5a5a', borderBottom: '2px solid #5a5a5a', color: '#2b2b2b' }}>
                    {w}
                  </button>
                  <button onClick={() => deleteWorld(w)} title="Borrar mundo" className="px-3 py-2 text-sm"
                    style={{ background: '#c98a8a', borderTop: '2px solid #f3d3d3', borderLeft: '2px solid #f3d3d3', borderRight: '2px solid #7a4a4a', borderBottom: '2px solid #7a4a4a', color: '#3a1010' }}>
                    Borrar
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={newWorldName} onChange={(e) => setNewWorldName(e.target.value)}
                placeholder="Nombre del mundo nuevo"
                onKeyDown={(e) => { if (e.key === 'Enter' && newWorldName.trim()) playWorld(newWorldName); }}
                className="flex-1 px-3 py-2 text-sm outline-none"
                style={{ background: '#ffffff', borderTop: '2px solid #8f8f8f', borderLeft: '2px solid #8f8f8f', borderRight: '2px solid #ededed', borderBottom: '2px solid #ededed', color: '#2b2b2b' }} />
              <button onClick={() => newWorldName.trim() && playWorld(newWorldName)}
                className="px-4 py-2 text-sm text-white"
                style={{ background: '#3b8f3b', borderTop: '2px solid #6fce6f', borderLeft: '2px solid #6fce6f', borderRight: '2px solid #1f5f1f', borderBottom: '2px solid #1f5f1f' }}>
                Crear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900/90 border border-gray-700 rounded-lg p-8 max-w-md mx-4">
            <div className="flex items-center space-x-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <div className="text-white">
                <h3 className="text-lg font-semibold">Loading Game</h3>
                <p className="text-gray-300 text-sm mt-1">Please wait while the game loads...</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Canvas container */}
      <div
        ref={canvasContainerRef}
        className="m-0 p-0"
        style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}
      >
        <canvas
          ref={canvasRef}
          id="canvas"
          className="emscripten"
          onContextMenu={(e) => e.preventDefault()}
          // PiolaCraft: en el iframe del hub el teclado de SDL (que escucha en la
          // ventana) no capturaba porque el iframe/canvas no retenía el foco. Al
          // clickear forzamos foco a la ventana Y al canvas para que los keydown
          // lleguen a la cola de SDL. tabIndex=0 hace el canvas realmente focusable.
          onMouseDown={() => {
            try { window.focus(); } catch (e) { /* */ }
            try { canvasRef.current?.focus(); } catch (e) { /* */ }
          }}
          tabIndex={0}
        ></canvas>
      </div>

      {/* Floating Console */}
      {showConsole && (
        <div className="absolute bottom-4 left-4 right-4 h-[30vh] z-10 bg-gray-900/85 backdrop-blur-sm border border-gray-700 rounded-lg shadow-xl overflow-hidden">
          <div className="flex items-center justify-between bg-gray-800/90 px-3 py-1.5">
            <h3 className="text-white text-sm font-medium">Console Output</h3>
            <button
              onClick={toggleConsole}
              className="text-gray-400 hover:text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <textarea
            id="console_output"
            className="w-full h-[calc(100%-32px)] bg-transparent text-green-400 font-mono p-3 resize-none focus:outline-none"
            readOnly
            value={minetestConsole.messages.join('\n')}
          ></textarea>
        </div>
      )}

      <SnackBar />
    </div>
  );
};

export default RuntimeScreen; 